'use client';

import { FC, useCallback, useState } from 'react';

/** Use proxy for B2 receipts to avoid CORS/private bucket issues */
function getViewerUrl(url: string): string {
  if (url.includes('/expenses/')) {
    return `/api/receipt?url=${encodeURIComponent(url)}`;
  }
  return url;
}

/** Image with fallback to direct URL when proxy fails */
const ReceiptImage: FC<{
  src: string;
  alt: string;
  className?: string;
}> = ({ src, alt, className }) => {
  const displaySrc =
    src.startsWith('http') && src.includes('/expenses/')
      ? getViewerUrl(src)
      : src;
  const [useDirect, setUseDirect] = useState(false);
  const imgSrc = useDirect && src.startsWith('http') ? src : displaySrc;

  return (
    <img
      src={imgSrc}
      alt={alt}
      className={className}
      onError={() => {
        if (!useDirect && src.startsWith('http')) setUseDirect(true);
      }}
    />
  );
};

import { Upload, X, Camera, FileImage, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface ReceiptUploadProps {
  onFileSelect: (file: File) => void;
  currentFile?: File | null;
  currentUrl?: string | null;
  onRemove: () => void;
  disabled?: boolean;
}

export const ReceiptUpload: FC<ReceiptUploadProps> = ({
  onFileSelect,
  currentFile,
  currentUrl,
  onRemove,
  disabled = false,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [preview, setPreview] = useState<string | null>(
    currentFile ? URL.createObjectURL(currentFile) : currentUrl || null
  );
  const [previewIsPdf, setPreviewIsPdf] = useState(
    currentFile?.type === 'application/pdf' ||
      (!!currentUrl && currentUrl.toLowerCase().endsWith('.pdf'))
  );

  const handleFile = useCallback(
    (file: File) => {
      // Validate file type
      if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
        alert('Please upload an image or PDF file');
        return;
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB');
        return;
      }

      onFileSelect(file);

      // Create preview for images and PDFs
      if (file.type.startsWith('image/')) {
        setPreviewIsPdf(false);
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreview(reader.result as string);
        };
        reader.readAsDataURL(file);
      } else if (file.type === 'application/pdf') {
        setPreviewIsPdf(true);
        setPreview((prev) => {
          if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
          return URL.createObjectURL(file);
        });
      } else {
        setPreviewIsPdf(false);
        setPreview(null);
      }
    },
    [onFileSelect]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const file = e.dataTransfer.files[0];
      if (file) {
        handleFile(file);
      }
    },
    [handleFile]
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleFile(file);
      }
    },
    [handleFile]
  );

  const handleCameraClick = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment'; // Use back camera on mobile
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        handleFile(file);
      }
    };
    input.click();
  }, [handleFile]);

  const handleRemove = useCallback(() => {
    if (preview?.startsWith('blob:')) {
      URL.revokeObjectURL(preview);
    }
    setPreview(null);
    setPreviewIsPdf(false);
    onRemove();
  }, [onRemove, preview]);

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium">Receipt/Invoice</label>
      <div
        className={cn(
          'relative border-2 border-dashed rounded-lg p-6 transition-colors',
          isDragging
            ? 'border-primary bg-primary/5'
            : 'border-gray-300 dark:border-gray-700',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {preview ? (
          <div className="relative">
            <div className="relative w-full h-48 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800">
              {previewIsPdf ? (
                <div className="flex flex-col items-center justify-center w-full h-full gap-2">
                  <FileText className="h-12 w-12 text-gray-400" />
                  <span className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-50">
                    PDF
                  </span>
                </div>
              ) : (
                <ReceiptImage
                  src={preview}
                  alt="Receipt preview"
                  className="w-full h-full object-contain"
                />
              )}
            </div>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              className="absolute top-2 right-2"
              onClick={handleRemove}
              disabled={disabled}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center space-y-4">
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800">
              <FileImage className="h-8 w-8 text-gray-400" />
            </div>
            <div className="text-center">
              <p className="text-sm font-medium">
                Drag and drop your receipt here
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                or click to browse
              </p>
            </div>
            <div className="flex gap-2">
              <label>
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={handleFileInput}
                  disabled={disabled}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={disabled}
                  asChild
                >
                  <span>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload
                  </span>
                </Button>
              </label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleCameraClick}
                disabled={disabled}
              >
                <Camera className="h-4 w-4 mr-2" />
                Camera
              </Button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Supports JPG, PNG, PDF (max 10MB)
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
